// src/data/calendarEvents.ts
// -----------------------------
// Repository for `calendar_events` (Resources → Calendar; db-108). Internal
// staff read; Supervisors + SuperUsers write (enforced by RLS). No hard delete —
// removal is a soft delete via `deactivated`. Recurrence is expanded CLIENT-side
// (src/services/calendar.ts), so we fetch the full active set (the roster is
// small) and let the calendar filter to the visible range.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type CalendarEventRow = Database["public"]["Tables"]["calendar_events"]["Row"];
export type CalendarEventInsert = Database["public"]["Tables"]["calendar_events"]["Insert"];
export type CalendarEventUpdate = Database["public"]["Tables"]["calendar_events"]["Update"];
export type CalendarEventExceptionRow =
  Database["public"]["Tables"]["calendar_event_exceptions"]["Row"];
export type CalendarEventExceptionInsert =
  Database["public"]["Tables"]["calendar_event_exceptions"]["Insert"];

const COLS =
  "id, title, staff_id, color, all_day, starts_at, ends_at, recurrence, recur_until, description, deactivated";
const EXC_COLS =
  "id, event_id, occurrence_date, cancelled, title, staff_id, color, all_day, starts_at, ends_at, description, deactivated";

// All active (not deactivated) events, earliest first. Null on error / no client.
export async function listCalendarEvents(): Promise<CalendarEventRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("calendar_events")
    .select(COLS)
    .eq("deactivated", false)
    .order("starts_at", { ascending: true });
  if (error) {
    logger.warn("listCalendarEvents failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as CalendarEventRow[];
}

export async function createCalendarEvent(
  row: CalendarEventInsert
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: { message: "No database connection." } };
  const { error } = await c.from("calendar_events").insert(row);
  if (error) logger.warn("createCalendarEvent failed:", error.message);
  return { error };
}

export async function updateCalendarEvent(
  id: string,
  patch: CalendarEventUpdate
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: { message: "No database connection." } };
  const { error } = await c
    .from("calendar_events")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) logger.warn("updateCalendarEvent failed:", error.message);
  return { error };
}

// Soft delete (schema convention: never hard-delete).
export async function deactivateCalendarEvent(
  id: string
): Promise<{ error: { message: string } | null }> {
  return updateCalendarEvent(id, { deactivated: true });
}

// ── Per-instance exceptions (db-111) ──────────────────────────────────────────
// Overrides/cancellations of single recurring instances. Small table — fetch the
// whole active set and let src/services/calendar.ts apply them during expansion.
export async function listCalendarEventExceptions(): Promise<CalendarEventExceptionRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("calendar_event_exceptions")
    .select(EXC_COLS)
    .eq("deactivated", false);
  if (error) {
    logger.warn("listCalendarEventExceptions failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as CalendarEventExceptionRow[];
}

// Upsert the active exception for (event_id, occurrence_date): update it if one
// exists, else insert. Used for both a single-instance edit (cancelled:false +
// override columns) and a single-instance delete (cancelled:true).
export async function upsertCalendarEventException(
  fields: CalendarEventExceptionInsert
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: { message: "No database connection." } };
  const { data: existing, error: findErr } = await c
    .from("calendar_event_exceptions")
    .select("id")
    .eq("event_id", fields.event_id)
    .eq("occurrence_date", fields.occurrence_date)
    .eq("deactivated", false)
    .limit(1);
  if (findErr) {
    logger.warn("upsertCalendarEventException find failed:", findErr.message);
    return { error: findErr };
  }
  if (existing && existing.length > 0) {
    const { error } = await c
      .from("calendar_event_exceptions")
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq("id", (existing[0] as { id: string }).id);
    if (error) logger.warn("upsertCalendarEventException update failed:", error.message);
    return { error };
  }
  const { error } = await c.from("calendar_event_exceptions").insert(fields);
  if (error) logger.warn("upsertCalendarEventException insert failed:", error.message);
  return { error };
}

// End a weekly series the day BEFORE `occurrenceDate` (recur_until is inclusive).
// The "This and following" edit calls this on the master, then createCalendarEvent
// for the new series that starts at the edited occurrence.
export async function endCalendarSeriesBefore(
  id: string,
  occurrenceDate: string
): Promise<{ error: { message: string } | null }> {
  const d = new Date(`${occurrenceDate}T00:00:00`);
  d.setDate(d.getDate() - 1);
  const until = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
  return updateCalendarEvent(id, { recur_until: until });
}
